import { useContext, useEffect, useState, useCallback } from 'react';
import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { tags as tagsApi, tag as tagApi } from 'utilities/api';
import { colorsMatch, mapFromStandardColor } from 'constants/colors';

const useTagsData = () => {
  const [, dispatch] = useContext(Context);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getTags = useCallback(() => {
    const { promise, cancel } = tagsApi.fetch(null, { size: -1 });
    promise
      .then(({ data }) => {
        // Sort tags alphabetically by name
        const sortedTags = [...data.results].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setTags(sortedTags);
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => setLoading(false));

    return cancel;
  }, [dispatch]);

  const saveTags = useCallback(
    async (formValues, enhancedTags) => {
      setSaving(true);

      try {
        // Separate create, update, and delete operations
        const createPromises = [];
        const updatePromises = [];
        const deletePromises = [];

        // Handle creates and updates
        Object.entries(formValues.tags).forEach(([tagId, tagData]) => {
          if (tagData.source === 'create') {
            // New tag - only create if name is not empty
            if (tagData.name && tagData.name.trim().length > 0) {
              const tagForApi = {
                name: tagData.name.trim(),
                description: '',
                color: mapFromStandardColor(tagData.color)
              };
              const { promise } = tagApi.create(tagForApi);
              createPromises.push(promise);
            }
            // Skip creating tags with empty names
          } else {
            // Existing tag - check if it needs updating
            const originalTag = enhancedTags.find(t => t.id === tagId);
            if (
              originalTag &&
              (originalTag.name !== tagData.name ||
                !colorsMatch(originalTag.color, tagData.color))
            ) {
              // Tag was modified - update
              const tagForApi = {
                name: tagData.name.trim(),
                description: '',
                color: mapFromStandardColor(tagData.color)
              };
              const { promise } = tagApi.update(tagId, tagForApi);
              updatePromises.push(promise);
            }
          }
        });

        // Handle deletes - find tags that exist in enhancedTags but not in formValues.tags
        const formTagIds = new Set(Object.keys(formValues.tags || {}));
        const deletedTags = enhancedTags.filter(tag => !formTagIds.has(tag.id));

        deletedTags.forEach(tag => {
          const { promise } = tagApi.delete(tag.id);
          deletePromises.push(promise);
        });

        // Execute all operations
        const createResults = await Promise.allSettled(createPromises);
        const updateResults = await Promise.allSettled(updatePromises);
        const deleteResults = await Promise.allSettled(deletePromises);

        // Check for failures
        const failures = [
          ...createResults,
          ...updateResults,
          ...deleteResults
        ].filter(result => result.status === 'rejected');
        const successes = [
          ...createResults,
          ...updateResults,
          ...deleteResults
        ].filter(result => result.status === 'fulfilled');
        const createSuccesses = createResults.filter(
          result => result.status === 'fulfilled'
        );
        const updateSuccesses = updateResults.filter(
          result => result.status === 'fulfilled'
        );
        const deleteSuccesses = deleteResults.filter(
          result => result.status === 'fulfilled'
        );

        // Refresh tags data from API (even if some failed, we want to show what succeeded)
        await getTags();

        const returnValue = {
          createSuccesses,
          updateSuccesses,
          deleteSuccesses,
          success: false,
          partialSuccess: false,
          error: null
        };

        if (failures.length === 0) {
          returnValue.success = true;
          // All succeeded
          return returnValue;
        }
        if (successes.length > 0) {
          // Partial success - some saved, some failed
          returnValue.partialSuccess = true;
          returnValue.error = new Error('Some tags failed to save');
          return returnValue;
        }
        // Total failure - none saved
        returnValue.error = new Error('All tags failed to save');
        return returnValue;
      } catch (error) {
        // Handle error with proper error handling
        parseServerError(dispatch)(error);
        return {
          success: false,
          partialSuccess: false,
          createSuccesses: [],
          updateSuccesses: [],
          deleteSuccesses: [],
          error
        };
      } finally {
        setSaving(false);
      }
    },
    [dispatch, getTags]
  );

  useEffect(() => {
    const cancel = getTags();

    return () => {
      cancel();
    };
  }, [getTags]);

  return {
    tags,
    loading,
    saving,
    getTags,
    saveTags
  };
};

export default useTagsData;
